import { useEffect } from 'react';
import {
  useLead,
  useLeadProjects,
  usePracticeLabs,
  useTasksFromProjectIdArray,
} from '@witmetrics/data-store';
import { mapToId } from '@witmetrics/utils/arrays';
import { useAppState } from '@/providers/AppStateProvider';
import { usePath } from '@/hooks/usePath';
import { useTab } from '@/hooks/useTab';
import TabsBlock, { type TabChild } from '@/components/TabsBlock';
import Card from '@/components/Card';
import CasesTab from '../tabs/CasesTab';
import TasksTab from '../tabs/TasksTab';
import OrdersTab from '../tabs/OrdersTab';
import type { PatientOverlayTab } from '../types';
import { classes, getDefaultTab } from './utils';

export type TabsColumnProps = {
  isFetchingData: boolean;
};

export default function TabsColumn({ isFetchingData }: TabsColumnProps) {
  const { activeLeadId } = useAppState();
  const { searchParams, pushParams } = usePath();
  const lead = useLead(activeLeadId);
  const practiceLabs = usePracticeLabs();
  const projects = useLeadProjects(activeLeadId);
  const tasks = useTasksFromProjectIdArray(mapToId(projects));
  const [defaultTab, setDefaultTab] = useTab<PatientOverlayTab>(
    getDefaultTab(searchParams)
  );

  useEffect(() => {
    const tab = searchParams.get('patientOverlayTab');
    if (tab !== null && tab !== defaultTab) {
      handleSelectTab(tab as PatientOverlayTab);
    }
  }, [searchParams]);

  const handleSelectTab = (tab: PatientOverlayTab) => {
    setDefaultTab(tab);
    pushParams({ patientOverlayTab: tab });
  };

  const renderTasksAdornment = () => {
    const openTasks = tasks.filter(({ task }) => !task.isResolved).length;
    if (openTasks === 0) return null;
    return <div className={classes.adornment}>{openTasks}</div>;
  };

  if (!lead) return null;

  let tabs: TabChild[] = [
    <CasesTab
      key="cases"
      isFetchingData={isFetchingData}
      name="cases"
      label="Cases"
    />,
    <TasksTab
      key="tasks"
      isFetchingData={isFetchingData}
      name="tasks"
      label="Tasks"
      adornment={renderTasksAdornment()}
    />,
  ];

  if (practiceLabs.length > 0) {
    tabs.push(
      <OrdersTab
        key="orders"
        isFetchingData={isFetchingData}
        name="orders"
        label="Orders"
      />
    );
  }

  return (
    <Card className={classes.card}>
      <TabsBlock<PatientOverlayTab[]>
        className={classes.tabsBlock}
        name="patient-info"
        bodyClassName={classes.tabBody}
        defaultTab={defaultTab}
        onChange={handleSelectTab}>
        {tabs}
      </TabsBlock>
    </Card>
  );
}
